/**
 * Lifted-in-spirit from cs-core's WinningLine + WindowPosition.
 * A payline is an ordered sequence of (col, row) coords across the visible
 * slot grid. {@code col} indexes the reel, {@code row} indexes the visible
 * window from the top.
 */
export interface WindowPosition {
  col: number;
  row: number;
}

export interface WinLine {
  /** Stable id used for logging and analytics. */
  id: string;
  positions: readonly WindowPosition[];
}

/**
 * Standard 5-line config for a 3-reel × 3-row slot:
 *   1. top row, 2. middle row, 3. bottom row,
 *   4. diagonal top-left → bottom-right,
 *   5. diagonal bottom-left → top-right.
 *
 * Lifted from the prevailing convention in cs-core's MMS reel windows; matches
 * what most casual-slot players intuit when they see a 3×3 grid.
 */
export const STANDARD_3X3_WIN_LINES: readonly WinLine[] = [
  { id: 'top', positions: [{ col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 }] },
  { id: 'middle', positions: [{ col: 0, row: 1 }, { col: 1, row: 1 }, { col: 2, row: 1 }] },
  { id: 'bottom', positions: [{ col: 0, row: 2 }, { col: 1, row: 2 }, { col: 2, row: 2 }] },
  { id: 'diag-down', positions: [{ col: 0, row: 0 }, { col: 1, row: 1 }, { col: 2, row: 2 }] },
  { id: 'diag-up', positions: [{ col: 0, row: 2 }, { col: 1, row: 1 }, { col: 2, row: 0 }] },
];

/**
 * Standard 10-line config for a 5-reel × 3-row slot — the MMS layout.
 *
 * <p>cs-core's Murder Mystery Slots ships these patterns. They cover the
 * three straight rows, two outward V-shapes, two staircase patterns, and
 * three zigzags — every position on the visible grid touches at least one
 * line so a single high-paying symbol is never "wasted".
 *
 * <p>Layout (rows 0=top, 2=bottom, 5 reels left-to-right):
 * <ul>
 *   <li>Line 1 — top row (0,0)(1,0)(2,0)(3,0)(4,0)</li>
 *   <li>Line 2 — middle row (0,1)(1,1)(2,1)(3,1)(4,1)</li>
 *   <li>Line 3 — bottom row (0,2)(1,2)(2,2)(3,2)(4,2)</li>
 *   <li>Line 4 — V down  (0,0)(1,1)(2,2)(3,1)(4,0)</li>
 *   <li>Line 5 — V up    (0,2)(1,1)(2,0)(3,1)(4,2)</li>
 *   <li>Line 6 — descending stairs (0,0)(1,0)(2,1)(3,2)(4,2)</li>
 *   <li>Line 7 — ascending stairs  (0,2)(1,2)(2,1)(3,0)(4,0)</li>
 *   <li>Line 8 — M shape (0,1)(1,0)(2,1)(3,2)(4,1)</li>
 *   <li>Line 9 — W shape (0,1)(1,2)(2,1)(3,0)(4,1)</li>
 *   <li>Line 10 — top zigzag (0,0)(1,1)(2,0)(3,1)(4,0)</li>
 * </ul>
 */
export const STANDARD_5X3_WIN_LINES: readonly WinLine[] = [
  { id: 'top', positions: [{ col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 }, { col: 3, row: 0 }, { col: 4, row: 0 }] },
  { id: 'middle', positions: [{ col: 0, row: 1 }, { col: 1, row: 1 }, { col: 2, row: 1 }, { col: 3, row: 1 }, { col: 4, row: 1 }] },
  { id: 'bottom', positions: [{ col: 0, row: 2 }, { col: 1, row: 2 }, { col: 2, row: 2 }, { col: 3, row: 2 }, { col: 4, row: 2 }] },
  { id: 'v-down', positions: [{ col: 0, row: 0 }, { col: 1, row: 1 }, { col: 2, row: 2 }, { col: 3, row: 1 }, { col: 4, row: 0 }] },
  { id: 'v-up', positions: [{ col: 0, row: 2 }, { col: 1, row: 1 }, { col: 2, row: 0 }, { col: 3, row: 1 }, { col: 4, row: 2 }] },
  { id: 'stairs-down', positions: [{ col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 1 }, { col: 3, row: 2 }, { col: 4, row: 2 }] },
  { id: 'stairs-up', positions: [{ col: 0, row: 2 }, { col: 1, row: 2 }, { col: 2, row: 1 }, { col: 3, row: 0 }, { col: 4, row: 0 }] },
  { id: 'm-shape', positions: [{ col: 0, row: 1 }, { col: 1, row: 0 }, { col: 2, row: 1 }, { col: 3, row: 2 }, { col: 4, row: 1 }] },
  { id: 'w-shape', positions: [{ col: 0, row: 1 }, { col: 1, row: 2 }, { col: 2, row: 1 }, { col: 3, row: 0 }, { col: 4, row: 1 }] },
  { id: 'top-zigzag', positions: [{ col: 0, row: 0 }, { col: 1, row: 1 }, { col: 2, row: 0 }, { col: 3, row: 1 }, { col: 4, row: 0 }] },
];
